async function tbAllowKlaviyoPhone(email, phone) {
  if (!phone) return false;
  try {
    const r = await fetch(
      "https://teddy-baldassarre.vercel.app/api/klaviyo-consent-check",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, phone }),
      }
    );
    if (!r.ok) return false;
    const d = await r.json();
    return d.allowPhone === true;
  } catch (err) {
    console.warn("Klaviyo consent check failed, withholding phone", err);
    return false;
  }
}

class EbookHero extends HTMLElement {
  connectedCallback() {
    this.pdfUrl = this.dataset.pdfUrl;
    this.listId = this.dataset.listId || "";
    this.emailSource = this.dataset.emailSource || "";
    this.smsSource = this.dataset.smsSource || "";

    this.flipbookEl = this.querySelector(".ebook-hero__flipbook");
    this.loadingEl = this.querySelector(".ebook-hero__loading");
    this.prevBtn = this.querySelector(".ebook-hero__nav-btn--prev");
    this.nextBtn = this.querySelector(".ebook-hero__nav-btn--next");
    this.endOverlay = this.querySelector(".ebook-hero__end-overlay");

    this.form = this.querySelector(".ebook-hero__form");
    this.errorEl = this.querySelector(".ebook-hero__error");
    this.signupContent = this.querySelector(".ebook-hero__signup-content");
    this.successBlock = this.querySelector(".ebook-hero__success");

    this.step1El = this.querySelector(".ebook-hero__step-1");
    this.step2El = this.querySelector(".ebook-hero__step-2");
    this.nextStepBtn = this.querySelector(".ebook-hero__next-step");
    this.submitBtnFinal = this.querySelector(".ebook-hero__submit-final");

    this.pageFlip = null;

    if (this.pdfUrl) {
      this.initFlipbook();
    }

    this.nextStepBtn?.addEventListener("click", () => this.goToStep2());
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    }

    this.prevBtn?.addEventListener("click", () => this.flipPrev());
    this.nextBtn?.addEventListener("click", () => this.flipNext());
  }

  async initFlipbook() {
    // Wait for PDF.js and StPageFlip to be available
    await this.waitForLibs();

    try {
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";

      const pdf = await pdfjsLib.getDocument(this.pdfUrl).promise;
      const scale = Math.max(window.devicePixelRatio || 1, 2);

      // Render first page to get aspect ratio and reserve height immediately
      const firstPage = await pdf.getPage(1);
      const firstViewport = firstPage.getViewport({ scale });
      const pageRatio = firstViewport.height / firstViewport.width;

      const isMobile = window.innerWidth < 768;
      const containerWidth = this.flipbookEl.offsetWidth;
      const pageWidth = isMobile
        ? containerWidth
        : Math.floor(containerWidth / 2);
      const pageHeight = Math.floor(pageWidth * pageRatio);

      // Render all pages to data URLs
      const imageUrls = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = i === 1 ? firstPage : await pdf.getPage(i);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;
        imageUrls.push(canvas.toDataURL("image/png"));
      }

      if (this.loadingEl) this.loadingEl.style.display = "none";

      // Build page DOM elements for HTML rendering (Retina-sharp)
      for (const url of imageUrls) {
        const pageDiv = document.createElement("div");
        pageDiv.className = "ebook-hero__page";
        const img = document.createElement("img");
        img.src = url;
        img.draggable = false;
        pageDiv.appendChild(img);
        this.flipbookEl.appendChild(pageDiv);
      }

      this.pageFlip = new St.PageFlip(this.flipbookEl, {
        width: pageWidth,
        height: pageHeight,
        size: "stretch",
        minWidth: 280,
        maxWidth: 533,
        minHeight: 350,
        maxHeight: 1200,
        showCover: false,
        drawShadow: true,
        flippingTime: 700,
        usePortrait: true,
        startZIndex: 0,
        autoSize: true,
        maxShadowOpacity: 0.4,
        mobileScrollSupport: true,
      });

      this.pageFlip.loadFromHTML(
        this.flipbookEl.querySelectorAll(".ebook-hero__page")
      );

      // Disable left arrow on first page
      if (this.prevBtn) this.prevBtn.disabled = true;

      // Show/hide end-of-preview overlay on last page + update arrow states
      this.pageFlip.on("flip", (e) => {
        const totalPages = this.pageFlip.getPageCount();
        const isPortrait = this.pageFlip.getOrientation() === "portrait";
        const threshold = isPortrait ? totalPages - 1 : totalPages - 2;
        this.toggleEndOverlay(e.data >= threshold);
        this.updateNavButtons(e.data, totalPages, isPortrait);
      });

      // Remove CSS aspect-ratio reservation now that StPageFlip controls sizing
      this.flipbookEl.style.aspectRatio = "";

    } catch (err) {
      console.error("Flipbook init error:", err);
      if (this.loadingEl) this.loadingEl.textContent = "Unable to load preview.";
    }
  }

  async waitForLibs() {
    const maxWait = 15000;
    const start = Date.now();
    while (
      (typeof pdfjsLib === "undefined" || typeof St === "undefined") &&
      Date.now() - start < maxWait
    ) {
      await new Promise((r) => setTimeout(r, 100));
    }
    if (typeof pdfjsLib === "undefined") throw new Error("PDF.js not loaded");
    if (typeof St === "undefined") throw new Error("StPageFlip not loaded");
  }

  toggleEndOverlay(show) {
    if (!this.endOverlay) return;
    if (show) {
      this.endOverlay.classList.replace("opacity-0", "opacity-100");
      this.endOverlay.classList.remove("pointer-events-none");
    } else {
      this.endOverlay.classList.replace("opacity-100", "opacity-0");
      this.endOverlay.classList.add("pointer-events-none");
    }
  }

  updateNavButtons(currentPage, totalPages, isPortrait) {
    if (this.prevBtn) this.prevBtn.disabled = currentPage === 0;
    if (this.nextBtn) {
      const lastPage = isPortrait ? totalPages - 1 : totalPages - 2;
      this.nextBtn.disabled = currentPage >= lastPage;
    }
  }

  flipPrev() {
    if (this.pageFlip) this.pageFlip.flipPrev();
  }

  flipNext() {
    if (this.pageFlip) this.pageFlip.flipNext();
  }

  /* ── Klaviyo Form ── */

  setLoading(btn, isLoading) {
    if (!btn) return;
    if (isLoading) {
      btn._origText = btn._origText || btn.innerHTML;
      btn.innerHTML = "Submitting...";
      btn.disabled = true;
    } else {
      btn.innerHTML = btn._origText || btn.innerHTML;
      btn.disabled = false;
    }
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.style.display = msg ? "block" : "none";
    this.errorEl.textContent = msg || "";
  }

  goToStep2() {
    this.showError("");
    const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    const email = this.querySelector('[name="email"]')?.value.trim() || "";

    if (!email) {
      this.showError("Please provide an email address.");
      return;
    }
    if (!isValidEmail(email)) {
      this.showError("Please provide a valid email address.");
      return;
    }

    this._email = email;
    const emailDisplay = this.querySelector('[name="email_display"]');
    if (emailDisplay) emailDisplay.value = email;

    this.step1El.classList.add("hidden");
    this.step2El.classList.remove("hidden");
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.showError("");

    const email = this._email;
    const inputTelIntl = this.querySelector("input-tel-intl");
    const phoneVal = inputTelIntl?.querySelector('input[type="tel"]')?.value.trim() || "";
    const smsConsent = !!this.querySelector('[name="sms_consent"]')?.checked;

    if (!phoneVal) {
      this.showError("Please provide a phone number.");
      return;
    }

    let fmtPhone = "";
    try {
      fmtPhone = inputTelIntl.format(phoneVal);
    } catch (e) {
      this.showError("Please provide a valid phone number.");
      return;
    }

    this.setLoading(this.submitBtnFinal, true);

    try {
      const properties = {};
      if (this.emailSource) properties["signup source - email"] = this.emailSource;
      if (this.smsSource) properties["signup source - sms"] = this.smsSource;

      const subscriptions = {
        email: { marketing: { consent: "SUBSCRIBED" } },
      };
      const allowPhone = await tbAllowKlaviyoPhone(email, fmtPhone);

      if (smsConsent && allowPhone) {
        subscriptions.sms = {
          marketing: { consent: "SUBSCRIBED" },
          transactional: { consent: "SUBSCRIBED" },
        };
      }

      const profileAttrs = { email, properties, subscriptions };
      if (fmtPhone && allowPhone) profileAttrs.phone_number = fmtPhone;

      const payload = {
        data: {
          type: "subscription",
          attributes: {
            profile: {
              data: { type: "profile", attributes: profileAttrs },
            },
          },
          ...(this.listId && {
            relationships: {
              list: {
                data: { type: "list", id: this.listId },
              },
            },
          }),
        },
      };

      const response = await fetch(
        "https://a.klaviyo.com/client/subscriptions/?company_id=JFDzfK",
        {
          method: "POST",
          headers: {
            Accept: "application/vnd.api+json",
            "Content-Type": "application/vnd.api+json",
            revision: "2025-01-15",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        let message = "Something went wrong. Please try again.";
        try {
          const errorData = await response.json();
          message = errorData.errors?.[0]?.detail || message;
        } catch (e) {}
        this.showError(message);
        this.setLoading(this.submitBtnFinal, false);
        return;
      }

      // Create/tag Shopify customer (non-blocking)
      const customerTag = this.dataset.customerTag;
      if (customerTag) {
        try {
          await fetch(
            "https://teddy-baldassarre.vercel.app/api/customer-with-tags",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                input: { email, phone: fmtPhone },
                tags: [customerTag],
              }),
            }
          );
        } catch (e) {
          console.error("Customer tag error:", e);
        }
      }

      // Leaf - Custom Event - generate_lead - Start
      try {
        window.LeafCustomEvents?.publishLead?.({
          leadType: "ebook_download",
          leadSource: this.emailSource || "ebook - landing page",
          email,
          phone: fmtPhone,
        });
      } catch (e) {
        // tracking must never break the form flow
      }
      // Leaf - Custom Event - generate_lead - End
      // Success
      if (this.signupContent) this.signupContent.classList.add("hidden");
      if (this.successBlock) this.successBlock.classList.remove("hidden");
      this.showError("");
      this.setLoading(this.submitBtnFinal, false);
    } catch (err) {
      console.error("Ebook signup error:", err);
      this.showError("An unexpected error occurred. Please try again.");
      this.setLoading(this.submitBtnFinal, false);
    }
  }
}

if (!customElements.get("ebook-hero")) {
  customElements.define("ebook-hero", EbookHero);
}
